import React from 'react';
import { Check, Clock, CreditCard, Calendar, Truck, CheckCircle } from 'lucide-react';

export type TimelineStatus =
  | 'submitted'
  | 'quoted'
  | 'approved'
  | 'waiting_payment'
  | 'paid'
  | 'provider_assigned'
  | 'booking_confirmed'
  | 'accepted'
  | 'in_progress'
  | 'completed';

interface WorkProgressTimelineProps {
  status: TimelineStatus;
  submittedAt?: string;
  quotedAt?: string;
  paidAt?: string;
  providerAssignedAt?: string;
  bookingConfirmedAt?: string;
  startedAt?: string;
  completedAt?: string;
  scheduledDate?: string;
  scheduledTime?: string;
  providerName?: string;
}

// Map backend approval_status / payment_status to our timeline status
export function resolveTimelineStatus(
  approvalStatus: string,
  paymentStatus?: string
): TimelineStatus {
  const s = (approvalStatus || '').toLowerCase();
  const p = (paymentStatus || '').toLowerCase();

  if (s === 'completed') return 'completed';
  if (s === 'in_progress') return 'in_progress';
  if (s === 'accepted') return 'accepted';
  if (s === 'provider_assigned' || s === 'booking_confirmed') {
    return p === 'completed' || p === 'paid' ? 'booking_confirmed' : 'provider_assigned';
  }
  if (s === 'customer_approved' || s === 'admin_approved') {
    return p === 'completed' || p === 'paid' ? 'paid' : 'waiting_payment';
  }
  if (s === 'quoted') return 'quoted';
  return 'submitted';
}

const WorkProgressTimeline: React.FC<WorkProgressTimelineProps> = ({
  status,
  submittedAt,
  quotedAt,
  paidAt,
  providerAssignedAt,
  bookingConfirmedAt,
  startedAt,
  completedAt,
  scheduledDate,
  scheduledTime,
  providerName,
}) => {
  const ORDER: TimelineStatus[] = [
    'submitted',
    'quoted',
    'waiting_payment',
    'paid',
    'provider_assigned',
    'booking_confirmed',
    'in_progress',
    'completed',
  ];
  const currentIdx = ORDER.indexOf(status);

  const stages = [
    {
      id: 'submitted',
      label: 'Quote Submitted',
      icon: Check,
      date: submittedAt,
    },
    {
      id: 'quoted',
      label: 'Price Generated & Approved',
      icon: Check,
      date: quotedAt,
    },
    {
      id: 'waiting_payment',
      label: 'Awaiting Payment',
      icon: CreditCard,
      date: undefined,
    },
    {
      id: 'paid',
      label: 'Payment Received',
      icon: CreditCard,
      date: paidAt,
    },
    {
      id: 'provider_assigned',
      label: 'Provider Assigned',
      icon: Truck,
      date: providerAssignedAt,
      details: providerName ? `Assigned to ${providerName}` : undefined,
    },
    {
      id: 'booking_confirmed',
      label: 'Booking Confirmed',
      icon: Calendar,
      date: bookingConfirmedAt,
      details:
        scheduledDate && scheduledTime
          ? `Scheduled: ${scheduledDate} at ${scheduledTime}`
          : undefined,
    },
    {
      id: 'in_progress',
      label: 'Work In Progress',
      icon: Clock,
      date: startedAt,
    },
    {
      id: 'completed',
      label: 'Work Complete',
      icon: CheckCircle,
      date: completedAt,
    },
  ];

  return (
    <div className="w-full">
      <div className="space-y-3">
        {stages.map((stage, index) => {
          const stageIdx = ORDER.indexOf(stage.id as TimelineStatus);
          const isComplete = stageIdx < currentIdx;
          const isCurrentStage = stageIdx === currentIdx;
          const Icon = stage.icon;

          return (
            <div key={stage.id} className="flex gap-3">
              <div className="flex flex-col items-center pt-0.5">
                <div
                  className={`relative flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all flex-shrink-0 ${
                    isComplete
                      ? 'border-green-500 bg-green-50'
                      : isCurrentStage
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-gray-50'
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 ${
                      isComplete
                        ? 'text-green-600'
                        : isCurrentStage
                        ? 'text-blue-600'
                        : 'text-gray-400'
                    }`}
                  />
                </div>
                {index < stages.length - 1 && (
                  <div
                    className={`w-0.5 mt-1 mb-1 ${
                      isComplete ? 'bg-green-400' : 'bg-gray-300'
                    }`}
                    style={{ height: '32px' }}
                  />
                )}
              </div>

              <div className="flex-1 pt-1 pb-2 min-w-0">
                <p
                  className={`text-sm font-semibold leading-snug break-words ${
                    isComplete
                      ? 'text-green-700'
                      : isCurrentStage
                      ? 'text-blue-700'
                      : 'text-gray-500'
                  }`}
                >
                  {stage.label}
                  {isCurrentStage && (
                    <span className="ml-2 text-xs font-normal text-blue-500">
                      (Current)
                    </span>
                  )}
                </p>
                {stage.details && (
                  <p className="text-xs text-gray-600 mt-0.5 break-words">
                    {stage.details}
                  </p>
                )}
                {stage.date && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(stage.date).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkProgressTimeline;
